import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/books/BooksAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useBooksUIContext } from "./BooksUIContext";
import { BooksFilter } from "./books-filter/BooksFilter";
import { BooksTable } from "./books-table/BooksTable";

export function BooksCard() {
  // Books UI Context
  const booksUIContext = useBooksUIContext();
  const booksUIProps = useMemo(() => {
    return {
      ids: booksUIContext.ids,
      setIds: booksUIContext.setIds,
      queryParams: booksUIContext.queryParams,
      openNewBookPage: booksUIContext.openNewBookPage,
    };
  }, [booksUIContext]);
    
  // Getting curret state of Book list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.books }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Books Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Kitap Listesini Excele Aktarır */
  const exportBooksToExcel = () => {
    dispatch(actions.getAllBooks(booksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 25, 25, 25, 25, 20, 20, 25];
      const columns = ["Adı", "Yazarı", "Yayınevi", "Kategorisi", "Türü", "Sınıf", "Sayfa Sayısı", "Ekleyen"];
      entities.forEach(entity => rows.push([
        entity.name,
        entity.author,
        entity.publisher,
        entity.categoryName,
        entity.typeName,
        entity.grade,
        entity.pageNumber,
        entity.createdBy
      ]));
      const filename = "Kitap Listesi"
      exportToExcel({ columns, rows, widths, filename });
    });
  }

  /** Belli Bir Kitap Listesini PDF e Aktarır */
  const exportBooksToPDF = () => {
    dispatch(actions.getAllBooks(booksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Yazarı",
          rows: entities.map(entity => entity.author)
        },
        {
          header: "Yayınevi",
          rows: entities.map(entity => entity.publisher)
        },
        {
          header: "Kategorisi",
          rows: entities.map(entity => entity.categoryName)
        },
        {
          header: "Türü",
          rows: entities.map(entity => entity.typeName)
        },
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Sayfa Sayısı",
          rows: entities.map(entity => entity.pageNumber)
        },
        {
          header: "Ekleyen",
          rows: entities.map(entity => entity.createdBy)
        }
      ];
  
      const filename = "Kitap Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Kitap Listesini Yazdırma Penceresini Açar */
  const printBooks = () => {
    dispatch(actions.getAllBooks(booksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Kitap Listesi";
      const properties = ["Adı", "Yazarı", "Yayınevi", "Kategorisi", "Türü", "Sınıf", "Sayfa Sayısı", "Ekleyen"];

      entities.forEach(entity => {
        data.push({
          "Adı": entity.name,
          "Yazarı": entity.author,
          "Yayınevi": entity.publisher,
          "Kategorisi": entity.categoryName,
          "Türü": entity.typeName,
          "Sınıf": entity.grade,
          "Sayfa Sayısı": entity.pageNumber,
          "Ekleyen": entity.createdBy
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Kitap Listesini Panoya Kopyalar */
  const copyBooks = () => {
    dispatch(actions.getAllBooks(booksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Kitap Listesi";
      const columns = ["Adı", "Yazarı", "Yayınevi", "Kategorisi", "Türü", "Sınıf", "Sayfa Sayısı", "Ekleyen"];
      entities.forEach(entity => rows.push([
        entity.name,
        entity.author,
        entity.publisher,
        entity.categoryName,
        entity.typeName,
        entity.grade,
        entity.pageNumber,
        entity.createdBy
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kitap Listesini Siler */
  const deleteBooks = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteBooks(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);

    if (isSuccess) {
      setTimeout(() => {        
        booksUIProps.setIds([]);
        dispatch(actions.getBooks(booksUIProps.queryParams));        
      }, 2500);
    }
  };
  
  const title = <>Kitap Listesi{booksUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{booksUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportBooksToExcel}
                exportToPDF={exportBooksToPDF}
                print={printBooks}
                copy={copyBooks}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Kitap"
                method={booksUIProps.openNewBookPage}
              />
            }
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteBooks(booksUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <BooksFilter />
        <BooksTable
          deleteBooks={deleteBooks}
        />
      </CardBody>
    </Card>
  );
}